import { useState, useEffect, useCallback, useMemo } from 'react';
import SavedItemsService from '../services/savedItemsService';
import { TokenService } from '../utils/TokenService';
import { useReviewerItems } from './useReviewerItems';

/**
 * useSavedPanel — shared state & data for the ที่บันทึกไว้ (Saved) panel,
 * used by both MobileLayout and DesktopLayout so the two stay in sync.
 *
 * Tabs: 'grid' (ตาราง) | 'map' (แผนที่) | 'following' (ติดตาม).
 *
 * Contexts:
 *   - No reviewer selected → the user's own saved items.
 *   - A reviewer selected (from the ติดตาม tab) → that reviewer's items,
 *     newest first, capped at MAP_LIMIT for the map.
 *
 * The grid reveals items 9 at a time (client-side); the map shows up to the
 * latest 200. Both feed off the same fetched arrays — no extra endpoints.
 */

const MAP_LIMIT = 200;

const hasCoords = (it) =>
  it &&
  it.latitude !== null && it.latitude !== undefined && it.latitude !== '' &&
  it.longitude !== null && it.longitude !== undefined && it.longitude !== '';

// The map overlay (MapOverlay) reads place-shaped fields — event_name / image_url
// / _source / id — not the saved-item fields. Reshape a saved row into the place
// shape so markers show the right name, photo and category accent. Reviewer items
// already arrive in place shape, so only the user's own saved items need this.
const savedItemToPlace = (s) => {
  const rawId = `${s.item_type}_${s.item_id}`;
  return {
    id: rawId,
    event_id: rawId,
    event_name: s.item_name || '',
    place_name: s.item_name || '',
    name: s.item_name || '',
    event_image_link: s.item_image_url || '',
    image_url: s.item_image_url || '',
    main_image_url: s.item_image_url || '',
    event_location: s.item_location || '',
    event_date: s.item_date || '',
    latitude: Number(s.latitude),
    longitude: Number(s.longitude),
    _source: s.item_type,
    type: s.item_type,
    main_category: s.item_type === 'venue' ? 'Venue' : s.item_type === 'review' ? 'Review' : 'Event',
    // keep the original saved fields for the desktop list
    item_id: s.item_id,
    item_type: s.item_type,
    item_name: s.item_name,
    item_image_url: s.item_image_url,
    item_location: s.item_location,
    is_going: s.is_going,
  };
};

export function useSavedPanel({ isAuthenticated, language = 'TH', active }) {
  const [savedTab, setSavedTab] = useState('grid'); // 'grid' | 'map' | 'following'
  const [selectedReviewer, setSelectedReviewer] = useState(null);
  // The item whose detail is shown inline inside the panel's tab area (null = list/grid)
  const [detailItem, setDetailItem] = useState(null);

  const [savedItems, setSavedItems] = useState([]);
  const [savedLoading, setSavedLoading] = useState(false);

  const [followedCreators, setFollowedCreators] = useState([]);
  const [followedLoading, setFollowedLoading] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  }, []);

  const fetchSavedItems = useCallback(() => {
    if (!isAuthenticated) return;
    setSavedLoading(true);
    SavedItemsService.getUserSavedItems(getAuthHeaders())
      .then((data) => setSavedItems(data.items || []))
      .catch((err) => console.error('[useSavedPanel] saved items fetch error:', err))
      .finally(() => setSavedLoading(false));
  }, [isAuthenticated, getAuthHeaders]);

  const fetchFollowedCreators = useCallback(async () => {
    if (!isAuthenticated) return;
    setFollowedLoading(true);
    try {
      const activeToken = TokenService.getAccessToken();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/users/following`, {
        headers: { Authorization: `Bearer ${activeToken}` },
      });
      if (res.ok) setFollowedCreators(await res.json());
    } catch (err) {
      console.warn('[useSavedPanel] followed creators fetch error:', err);
    } finally {
      setFollowedLoading(false);
    }
  }, [isAuthenticated]);

  // Load the user's own saved items whenever the panel is showing them.
  useEffect(() => {
    if (active && !selectedReviewer && (savedTab === 'grid' || savedTab === 'map')) {
      fetchSavedItems();
    }
  }, [active, selectedReviewer, savedTab, fetchSavedItems]);

  // Load followed creators when the ติดตาม tab is opened.
  useEffect(() => {
    if (active && savedTab === 'following') fetchFollowedCreators();
  }, [active, savedTab, fetchFollowedCreators]);

  // A reviewer's full body of work (only fetched when one is selected).
  const { items: reviewerItems, loading: reviewerLoading } = useReviewerItems(
    selectedReviewer?.id || null,
    language,
    selectedReviewer?.name || null
  );

  // Show / hide an item's inline detail view within the current tab's area.
  const openDetail = useCallback((it) => setDetailItem(it || null), []);
  const closeDetail = useCallback(() => setDetailItem(null), []);

  // Switching tabs always drops any open detail view.
  const changeTab = useCallback((tab) => {
    setDetailItem(null);
    setSavedTab(tab);
  }, []);

  // Clicking a followed reviewer jumps straight to the map with their items.
  const selectReviewer = useCallback((reviewer) => {
    setDetailItem(null);
    setSelectedReviewer(reviewer || null);
    setSavedTab('map');
  }, []);

  const clearReviewer = useCallback(() => {
    setDetailItem(null);
    setSelectedReviewer(null);
  }, []);

  const gridItems = selectedReviewer ? reviewerItems : savedItems;
  const gridLoading = selectedReviewer ? reviewerLoading : savedLoading;

  const mapItems = useMemo(() => {
    if (selectedReviewer) return reviewerItems.slice(0, MAP_LIMIT).filter(hasCoords);
    return savedItems.filter(hasCoords).map(savedItemToPlace);
  }, [selectedReviewer, reviewerItems, savedItems]);

  // Stable per reviewer so the shared map re-fits ONCE when their items arrive
  // (the Map already re-fits when the coordinate set changes) — this keeps the
  // click to "swap the markers on the existing map", not reload it.
  const mapFitKey = selectedReviewer
    ? `saved-reviewer-${selectedReviewer.id}`
    : `saved-own-${mapItems.length}`;

  return {
    savedTab,
    setSavedTab,
    changeTab,
    detailItem,
    openDetail,
    closeDetail,
    selectedReviewer,
    selectReviewer,
    clearReviewer,
    savedItems,
    savedLoading,
    followedCreators,
    followedLoading,
    fetchFollowedCreators,
    reviewerItems,
    reviewerLoading,
    gridItems,
    gridLoading,
    mapItems,
    mapFitKey,
    refreshSaved: fetchSavedItems,
  };
}

export default useSavedPanel;
